import { API_BASE_URL } from '@/utils/constants';
import HttpClient from '@/utils/http-client';
import { useState } from 'react';

export type DateRange = {
  start_date: string;
  end_date: string;
};

export type AuditScoreRange = {
  min: number;
  max: number;
};

export type QualificationQuestion = {
  capture_id: string;
  capture_output: string[] | number[] | boolean | { min: number; max: number };
};

export type ConversationFilters = {
  search_date?: DateRange;
  customer_id?: number;
  conversation_owner_ids?: number[];
  crm_status_ids?: number[];
  concern_categories?: string[];
  agent_pitch_gaps?: string[];
  intents?: string[];
  qualification_questions?: QualificationQuestion[];
  audit_score_range?: AuditScoreRange;
};

export type CallOverview = {
  total_calls: number;
  contact_count: number;
  detailed_calls: number;
  avg_call_duration: number;
};

export type BuyingIntentItem =
  | { intent: 'HIGH_INTENT'; count: number }
  | { intent: 'MODERATE'; count: number }
  | { intent: 'LOW_INTENT'; count: number }
  | { intent: 'NEUTRAL'; count: number }
  | { intent: 'NOT_AVAILABLE'; count: number };

export type LeadConcern = {
  category: string;
  count: number;
};

export type KeyInsightsResponse = {
  key_insights: any[];
  lead_concerns: LeadConcern[];
  agent_pitch_gaps: any[];
};

export type ZipInsightItem = {
  user_id?: number;
  insights: {
    sentiment: 'INCREASING' | 'DECREASING' | 'NEUTRAL';
    insight: string;
  }[];
};

export type CustomerListItem = {
  crm_status: string;
  customer_name: string;
  email: string;
  phone: string;
  total_conversations: number;
  last_conversation: string;
  intent: BuyingIntentItem['intent'];
  quality_score: number;
  assignees: { name: string; avatar_url: string }[];
  customer_id?: any;
};

export function useConversationsInsights(
  workspaceId: number,
  page: number,
  limit: number,
  filters: any,
  authToken?: string,
  sortField?: string,
  sortOrder?: 'asc' | 'desc',
) {
  const commonHeaders = {
    Authorization: `Bearer ${authToken}`,
    'api-client-key': process.env.NEXT_PUBLIC_PARTNER_API_CLIENT_KEY!,
    'api-client-secret': process.env.NEXT_PUBLIC_PARTNER_API_CLIENT_SECRET!,
    'Content-Type': 'application/json',
  };
  // Objection Categories
  const [objections, setObjections] = useState<string[]>([]);
  const [loadingObjections, setLoadingObjections] = useState(false);

  async function fetchObjections() {
    setLoadingObjections(true);
    try {
      if(authToken){
        const res = await fetch(
          `${API_BASE_URL}/v1/client/customer/insights/objection-categories/${workspaceId}`,
          {
            method: 'GET',
            headers: commonHeaders,
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        const body = await res.json();
        setObjections(body.data as string[]);
        return body.data as string[];
      } else {
        const { data } = await HttpClient.get(
          `${API_BASE_URL}/v1/customer/insights/objection-categories/${workspaceId}`,
        );
  
        // console.log('fetchObjections', data.data);
  
        setObjections(data.data);
        return data.data;
      }
    } catch (e) {
      console.error('fetchObjections error', e);
      setObjections([]);
      return [];
    } finally {
      setLoadingObjections(false);
    }
  }

  // Call Overview
  const [overview, setOverview] = useState<CallOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  async function fetchOverview(payload?: ConversationFilters) {
    setLoadingOverview(true);
    try {
      if(authToken){
        const res = await fetch(
          `${API_BASE_URL}/v1/client/customer/insights/call-overview/${workspaceId}`,
          {
            method: 'POST',
            headers: commonHeaders,
            body: JSON.stringify({ filters: payload ?? filters }),
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        const body = await res.json();
        setOverview(body.data as CallOverview);
        return body.data as CallOverview;
      } else {
        const { data } = await HttpClient.post(
          `${API_BASE_URL}/v1/customer/insights/call-overview/${workspaceId}`,
          { filters: payload || filters },
        );
        setOverview(data?.data);
        return data.data;
      }
      
    } catch (e) {
      console.error('fetchOverview error', e);
      setOverview(null);
      return null;
    } finally {
      setLoadingOverview(false);
    }
  }

  // Buying Intent Spread
  const [intentSpread, setIntentSpread] = useState<BuyingIntentItem[]>([]);
  const [loadingIntentSpread, setLoadingIntentSpread] = useState(false);

  async function fetchIntentSpread(payload?: ConversationFilters) {
    setLoadingIntentSpread(true);
    try {
      if(authToken){
        const res = await fetch(
          `${API_BASE_URL}/v1/client/customer/insights/buying-intent/spread/${workspaceId}`,
          {
            method: 'POST',
            headers: commonHeaders,
            body: JSON.stringify({ filters: payload ?? filters }),
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        const body = await res.json();
        setIntentSpread(body.data as BuyingIntentItem[]);
        return body.data as BuyingIntentItem[];
      } else {
        const { data } = await HttpClient.post(
          `${API_BASE_URL}/v1/customer/insights/buying-intent/spread/${workspaceId}`,
          { filters: payload || filters },
        );
        setIntentSpread(data.data);
        return data.data;
      }
      
    } catch (e) {
      console.error('fetchIntentSpread error', e);
      setIntentSpread([]);
      return [];
    } finally {
      setLoadingIntentSpread(false);
    }
  }

  // Key Insights
  const [keyInsights, setKeyInsights] = useState<KeyInsightsResponse | null>(
    null,
  );
  const [loadingKeyInsights, setLoadingKeyInsights] = useState(false);
  const [zipInsights, setZipInsights] = useState<ZipInsightItem[] | null>(
    null,
  );


  async function fetchKeyInsights(payload?: ConversationFilters) {
    setLoadingKeyInsights(true);
    try {
      if(authToken){
        const res = await fetch(
          `${API_BASE_URL}/v1/client/customer/insights/key-insights/${workspaceId}`,
          {
            method: 'POST',
            headers: commonHeaders,
            body: JSON.stringify({ filters: payload ?? filters }),
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        const body = await res.json();
        setKeyInsights(body.data as KeyInsightsResponse);
        setZipInsights(body.data?.key_insights?.data)
        return body.data as KeyInsightsResponse;
      } else {
        const { data } = await HttpClient.post(
          `${API_BASE_URL}/v1/customer/insights/key-insights/${workspaceId}`,
          { filters: payload || filters },
        );
        setKeyInsights(data.data);
        setZipInsights(data.data?.key_insights?.data)
        return data.data;
      }
    } catch (e) {
      console.error('fetchKeyInsights error', e);
      setKeyInsights(null);
      setZipInsights(null);
      return null;
    } finally {
      setLoadingKeyInsights(false);
    }
  }

  // Customer List
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);


  async function fetchCustomers(
    payload?: ConversationFilters,
    nextPage?: number
  ) {
    setLoadingCustomers(true);
    try {
      if (authToken){
         // optionally attach sort params
      const queryParams = new URLSearchParams(
        sortField && sortOrder
          ? { sort_field: sortField, sort_order: sortOrder }
          : {}
      ).toString();
      const url = `${API_BASE_URL}/v1/client/customer/insights/customer-list/${workspaceId}${
        queryParams ? `?${queryParams}` : ''
      }`;

      const res = await fetch(url, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({
          page: nextPage ?? page,
          limit,
          filters: payload ?? filters,
        }),
      });
      if (!res.ok) throw new Error(res.statusText);
      const body = await res.json();
      // body.data.data is the array of items
      const pageData = (body.data as any).data as CustomerListItem[];
      setCustomers(pageData);
      return pageData;
      } else {
        const { data } = await HttpClient.post(
          `${API_BASE_URL}/v1/customer/insights/customer-list/${workspaceId}`,
          { page: nextPage || page, limit, filters: payload || filters },
        );
        const pageData = data.data.data;
        setCustomers(pageData);
        return pageData;
      }
     
    } catch (e) {
      console.error('fetchCustomers error', e);
      setCustomers([]);
      return [];
    } finally {
      setLoadingCustomers(false);
    }
  }

  const [agentPitchGaps, setAgentPitchGaps] = useState<
    { id: string; label: string }[]
  >([]);
  const [loadingAgentPitchGaps, setLoadingAgentPitchGaps] = useState(false);

  async function fetchAgentPitchGaps() {
    setLoadingAgentPitchGaps(true);
    try {
      if(authToken){
        const res = await fetch(
          `${API_BASE_URL}/v1/client/customer/insights/agent-pitch-gaps/${workspaceId}`,
          {
            method: 'GET',
            headers: commonHeaders,
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        const body = await res.json();
        setAgentPitchGaps(body.data as { id: string; label: string }[]);
        return body.data as { id: string; label: string }[];
      } else {
        const { data } = await HttpClient.get(
          `${API_BASE_URL}/v1/customer/insights/agent-pitch-gaps/${workspaceId}`,
        );
        setAgentPitchGaps(data.data);
        return data.data;
      }
      
    } catch (e) {
      console.error('fetchAgentPitchGaps error', e);
      setAgentPitchGaps([]);
      return [];
    } finally {
      setLoadingAgentPitchGaps(false);
    }
  }

  return {
    // data
    objections,
    overview,
    intentSpread,
    keyInsights,
    zipInsights,
    customers,
    agentPitchGaps,

    // loading flags
    loadingObjections,
    loadingOverview,
    loadingIntentSpread,
    loadingKeyInsights,
    loadingCustomers,
    loadingAgentPitchGaps,

    // fetch functions
    fetchObjections,
    fetchOverview,
    fetchIntentSpread,
    fetchKeyInsights,
    fetchCustomers,
    fetchAgentPitchGaps,
  };
}
